import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, HardHat } from "lucide-react";
import { Button } from "../components/ui/button";
import { getPaymentStatus } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const res = await getPaymentStatus(sessionId);
        if (res.payment_status === "paid") {
          setState("paid");
          await checkAuth();
          return;
        }
        if (attempts.current >= 8) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        if (attempts.current >= 8) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, checkAuth]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#1D4ED8] text-white"><HardHat className="h-6 w-6" /></div>
      {state === "checking" && (
        <div className="mt-6 flex items-center gap-2 text-slate-600"><Loader2 className="h-5 w-5 animate-spin" /> Confirming your payment…</div>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 className="mt-6 h-14 w-14 text-emerald-500" />
          <h1 className="mt-4 font-display text-3xl font-700 text-[#0A2540]">Payment successful!</h1>
          <p className="mt-2 text-slate-600">Your render points have been added to your account.</p>
          <div className="mt-6 flex gap-3">
            <Button onClick={() => navigate("/dashboard")} className="bg-[#1D4ED8] hover:bg-[#1e40af] text-white">Go to dashboard</Button>
            <Button onClick={() => navigate("/#studio")} variant="outline" className="border-slate-300">Open 3D Studio</Button>
          </div>
        </>
      )}
      {state === "pending" && (
        <>
          <Loader2 className="mt-6 h-12 w-12 animate-spin text-[#1D4ED8]" />
          <h1 className="mt-4 font-display text-2xl font-700 text-[#0A2540]">Payment processing</h1>
          <p className="mt-2 text-slate-600">This is taking a moment. Your points will appear shortly — check your dashboard.</p>
          <Button onClick={() => navigate("/dashboard")} className="mt-6 bg-[#1D4ED8] text-white">Go to dashboard</Button>
        </>
      )}
      {state === "error" && (
        <>
          <XCircle className="mt-6 h-12 w-12 text-[#DC2626]" />
          <h1 className="mt-4 font-display text-2xl font-700 text-[#0A2540]">Couldn't confirm payment</h1>
          <p className="mt-2 text-slate-600">If you were charged, your points will still be added. Contact support if needed.</p>
          <Button onClick={() => navigate("/")} className="mt-6 bg-[#DC2626] text-white">Back home</Button>
        </>
      )}
    </div>
  );
};

export const PaymentCancel = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-slate-50 px-6 text-center">
      <XCircle className="h-14 w-14 text-slate-400" />
      <h1 className="mt-4 font-display text-2xl font-700 text-[#0A2540]">Checkout canceled</h1>
      <p className="mt-2 text-slate-600">No charge was made. You can pick a plan whenever you're ready.</p>
      <Button onClick={() => navigate("/#pricing")} className="mt-6 bg-[#DC2626] hover:bg-[#b91c1c] text-white">Back to pricing</Button>
    </div>
  );
};
